import { useState } from "react";
import { Share2, Copy, RefreshCw, ExternalLink, Check } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, shortUrl, cardUrl } from "@/lib/api";
import { InviteMessage } from "./InviteMessage";

const HANDLE_RE = /^[a-z0-9](?:[a-z0-9-]{1,28})[a-z0-9]$/;
const domain = () => shortUrl("").replace(/^https?:\/\//, "");

const HandleEditor = ({ handle, onSaved }) => {
  const [value, setValue] = useState(handle);
  const [busy, setBusy] = useState(false);
  const clean = value.trim().toLowerCase();
  const valid = HANDLE_RE.test(clean) && !clean.includes("--");
  const save = async (e) => {
    e.preventDefault();
    if (!valid || clean === handle) return;
    setBusy(true);
    try {
      onSaved(await api.setHandle(clean));
      toast.success("Your short link is updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save that name");
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={save} className="space-y-1.5" data-testid="handle-editor">
      <p className="eyebrow !text-slate-500">Your short name</p>
      <div className="flex gap-2 items-center">
        <div className="flex-1 min-w-0 flex items-center rounded-md border border-input bg-transparent pl-3 text-sm focus-within:ring-1 focus-within:ring-emerald-400/60">
          <span className="font-mono text-xs text-slate-500 shrink-0">{domain()}</span>
          <input value={value} onChange={(e) => setValue(e.target.value.replace(/\s/g, "-"))} maxLength={30}
            className="flex-1 min-w-0 w-full bg-transparent outline-none py-2 pr-2 font-mono text-xs text-slate-100" data-testid="handle-input" />
        </div>
        <Button type="submit" variant="outline" disabled={!valid || clean === handle || busy} className="bg-transparent" data-testid="handle-save-button">
          <Check size={14} /> Save
        </Button>
      </div>
      {!valid && value && <p className="text-[11px] text-amber-300" data-testid="handle-hint">3–30 letters, numbers or dashes.</p>}
    </form>
  );
};

export const ShareDialog = () => {
  const [info, setInfo] = useState(null);
  const link = info ? shortUrl(info.handle) : "";

  const onOpen = async (o) => {
    if (o && !info) {
      const [i, codes] = await Promise.all([api.shareInfo(), api.channelSources().catch(() => ({}))]);
      setInfo({ ...i, codes });
    }
  };
  const copy = async () => {
    await navigator.clipboard.writeText(link).catch(() => null);
    toast.success("Link copied");
  };
  const regenerate = async () => {
    const token = await api.regenerateShare();
    setInfo((i) => ({ ...i, token }));
    toast.success("Old long links no longer work. Your short link still does.");
  };

  return (
    <Dialog onOpenChange={onOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="rounded-full bg-white text-[#0B0F17] hover:bg-slate-200" data-testid="share-open-button"><Share2 size={14} /> Share</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto overflow-x-hidden grid-cols-[minmax(0,1fr)]" data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Share your bucketlist</DialogTitle>
          <DialogDescription>Friends can view your map, travel plans and life goals. They can't edit anything. Your contacts and trip notes stay private.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <Input readOnly value={link || "Creating link…"} className="font-mono text-sm" data-testid="share-link-input" onFocus={(e) => e.target.select()} />
          <Button onClick={copy} disabled={!info} className="bg-emerald-500 text-[#04120c] hover:bg-emerald-400" data-testid="share-copy-button"><Copy size={14} /> Copy</Button>
        </div>
        {info && <HandleEditor key={`he-${info.handle}`} handle={info.handle} onSaved={(handle) => setInfo((i) => ({ ...i, handle }))} />}
        {info && <InviteMessage key={`im-${info.handle}`} link={cardUrl(info.handle)} codes={info.codes} />}
        {info && <p className="text-[11px] text-slate-500 -mt-1">Messages use a link that shows a preview card of your map on Facebook and WhatsApp.</p>}
        <div className="flex justify-between">
          <Button variant="ghost" size="sm" onClick={regenerate} disabled={!info} className="text-slate-400" data-testid="share-regenerate-button"><RefreshCw size={13} /> New long link</Button>
          {info && (
            <a href={link} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-sm text-emerald-300 hover:text-emerald-200" data-testid="share-preview-link"><ExternalLink size={13} /> Preview</a>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
